import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { FilterDropdown } from '../components/common/FilterDropdown';
import { OrganisationTable } from '../components/organisations/OrganisationTable';
import type { ClientSortField } from '../components/organisations/OrganisationTable';
import { useTableSort, type SortAccessors } from '../utils/useTableSort';
import { isClientShielded } from '../utils/shieldPlatinum';
import { organisationService } from '../services/organisationService';
import type { BvOrganisation } from '../types/billValidationTypes';

const STATUS_OPTIONS = ['active', 'onboarding', 'lead'];
const SHIELD_OPTIONS = ['Yes', 'No'];
// Job 28: deactivated clients are hidden by default, which is what everyone wants
// day to day — but they have to be reachable to be re-activated.
const BV_OPTIONS = ['Active', 'Deactivated'];

const isShielded = isClientShielded;

// Module-level so the sort memo isn't invalidated on every render.
const SORT_ACCESSORS: SortAccessors<BvOrganisation, ClientSortField> = {
  name:    (o) => o.name,
  abn:     (o) => o.abn,
  broker:  (o) => o.broker,
  shield:  (o) => isShielded(o),
  sites:   (o) => o.site_count ?? 0,
  status:  (o) => o.status,
  created: (o) => o.created_at,
};

export function OrganisationListPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<BvOrganisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [brokerFilter, setBrokerFilter] = useState('');
  const [shieldFilter, setShieldFilter] = useState('');
  const [bvFilter, setBvFilter] = useState('Active');

  const load = useCallback(async () => {
    setLoading(true);
    // Deactivated clients are fetched too (job 28) so this list can show them and
    // offer re-activation — the Bill Validation filter below decides what's shown.
    const result = await organisationService.list({ includeInactive: true });
    if (result.success) setOrgs(result.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Brokers come from the loaded rows — the set of brokers with BV clients is
  // what's worth filtering by, and it costs no extra query.
  const brokerOptions = useMemo(
    () => [...new Set(orgs.map((o) => o.broker ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [orgs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      const matchSearch = !q
        || o.name.toLowerCase().includes(q)
        || (o.abn ?? '').toLowerCase().includes(q)
        || (o.broker ?? '').toLowerCase().includes(q);
      const matchStatus = !statusFilter || o.status === statusFilter;
      const matchBroker = !brokerFilter || o.broker === brokerFilter;
      const matchShield = !shieldFilter || isShielded(o) === (shieldFilter === 'Yes');
      // Blank means "either" — only reachable via Clear filters.
      const matchBv = !bvFilter || (o.bill_val === 'active') === (bvFilter === 'Active');
      return matchSearch && matchStatus && matchBroker && matchShield && matchBv;
    });
  }, [orgs, search, statusFilter, brokerFilter, shieldFilter, bvFilter]);

  const { sortField, sortDir, onSort, sorted } = useTableSort(filtered, SORT_ACCESSORS, 'name');

  // 'Active' is the default view, not a filter the user applied.
  const hasActiveFilters = !!(search || statusFilter || brokerFilter || shieldFilter) || bvFilter !== 'Active';
  const clearAllFilters = () => {
    setSearch(''); setStatusFilter(''); setBrokerFilter(''); setShieldFilter('');
    setBvFilter('Active');
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Entities</h1>
          <p className="text-sm text-gray-500 mt-0.5">Active and onboarding energy customers</p>
        </div>
        <Button onClick={() => navigate('/bill-validation/organisations/new')}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Client
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-8 h-9 w-52 text-sm"
            placeholder="Search name, ABN or broker…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <FilterDropdown label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        <FilterDropdown label="Broker" value={brokerFilter} onChange={setBrokerFilter} options={brokerOptions} searchable />
        <FilterDropdown label="Shield Platinum" value={shieldFilter} onChange={setShieldFilter} options={SHIELD_OPTIONS} />
        <FilterDropdown label="Bill Validation" value={bvFilter} onChange={setBvFilter} options={BV_OPTIONS} />
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500 gap-1" onClick={clearAllFilters}>
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-auto">
        <OrganisationTable
          organisations={sorted}
          loading={loading}
          sortField={sortField}
          sortDir={sortDir}
          onSort={onSort}
          onSelect={(org) => navigate(`/bill-validation/organisations/${org.id}`)}
          onOpenNewWindow={(org) => window.open(`/bill-validation/organisations/${org.id}`, '_blank', 'noopener,noreferrer')}
        />
      </div>

      <div className="px-1 text-xs text-gray-500">
        {loading
          ? '…'
          : `${sorted.length} client${sorted.length === 1 ? '' : 's'}${
              hasActiveFilters ? ` of ${orgs.length}` : ''
            }`}
      </div>
    </div>
  );
}
